import { useEffect, useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ChevronDown } from 'lucide-react';

const HERO_BG = "https://images.unsplash.com/photo-1717835429626-d6dd3f2c9afb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwzfHxJbmRpYW4lMjBtb2RlbCUyMHNhcmVlJTIwbHV4dXJ5JTIwZmFzaGlvbiUyMHBvcnRyYWl0JTIwcmVkJTIwZ29sZHxlbnwwfHx8fDE3NzI5ODYwODZ8MA&ixlib=rb-4.1.0&q=85";
const FABRIC_TEXTURE = "https://images.unsplash.com/photo-1612863234179-7e51d74c28a4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwyfHxyZWQlMjBzaWxrJTIwZmFicmljJTIwd2F2ZSUyMHRleHR1cmUlMjBjbG9zZSUyMHVwfGVufDB8fHx8MTc3Mjk4NjA4OHww&ixlib=rb-4.1.0&q=85";

// Gold Particles Component
const GoldParticles = () => {
  const particles = Array.from({ length: 30 }, (_, i) => ({
    id: i,
    size: Math.random() * 6 + 2,
    x: Math.random() * 100,
    y: Math.random() * 100,
    delay: Math.random() * 5,
    duration: Math.random() * 3 + 4,
  }));

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map((particle) => (
        <motion.div
          key={particle.id}
          className="absolute rounded-full bg-[#D4AF37]"
          style={{
            width: particle.size,
            height: particle.size,
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            filter: 'blur(1px)',
          }}
          animate={{
            y: [-20, 20, -20],
            x: [-10, 10, -10],
            opacity: [0.3, 0.8, 0.3],
            scale: [1, 1.2, 1],
          }}
          transition={{
            duration: particle.duration,
            repeat: Infinity,
            delay: particle.delay,
            ease: 'easeInOut',
          }}
        />
      ))}
    </div>
  );
};

export const Hero = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end start'],
  });

  const y = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.2]);

  const scrollToCollections = () => {
    document.querySelector('#collections')?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToNewArrivals = () => {
    document.querySelector('#new-arrivals')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="home"
      ref={containerRef}
      className="relative h-screen overflow-hidden"
      data-testid="hero-section"
    >
      {/* Parallax Background Image */}
      <motion.div
        className="absolute inset-0"
        style={{ y, scale }}
      >
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_BG})` }}
        />
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/60" />
      </motion.div>

      {/* Floating Silk Fabric Animation */}
      <motion.div
        className="absolute inset-0 opacity-20"
        animate={{
          backgroundPosition: ['0% 0%', '100% 100%', '0% 0%'],
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          ease: 'linear',
        }}
        style={{
          backgroundImage: `url(${FABRIC_TEXTURE})`,
          backgroundSize: 'cover',
          mixBlendMode: 'overlay',
        }}
      />

      {/* Gold Particles */}
      <GoldParticles />

      {/* Main Content */}
      <motion.div
        className="relative z-10 h-full flex flex-col items-center justify-center text-center px-4"
        style={{ opacity }}
      >
        {/* Brand Name */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.3 }}
          className="mb-6"
        >
          <h1 
            className="font-heading text-5xl md:text-7xl lg:text-8xl text-white tracking-tight"
            data-testid="hero-title"
          >
            <motion.span
              className="block"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
            >
              DEVANAND
            </motion.span>
            <motion.span
              className="block text-[#D4AF37]"
              style={{ textShadow: '2px 2px 8px rgba(0,0,0,0.8), 0px 0px 20px rgba(0,0,0,0.5)' }}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
            >
              VESTMENT
            </motion.span>
          </h1>
        </motion.div>

        {/* Tagline */}
        <motion.p
          className="font-accent text-lg md:text-xl lg:text-2xl text-[#D4AF37] tracking-[0.3em] uppercase mb-12"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.2 }}
          data-testid="hero-tagline"
        >
          Elegance Woven in Tradition
        </motion.p>

        {/* Decorative Line */}
        <motion.div
          className="w-32 h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent mb-12"
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 1, delay: 1.5 }}
        />

        {/* CTA Buttons */}
        <motion.div
          className="flex flex-col sm:flex-row gap-4 sm:gap-6"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.8 }}
        >
          <motion.button
            onClick={scrollToCollections}
            className="px-8 py-4 bg-[#800020] text-white font-body text-sm tracking-widest uppercase border border-[#800020] hover:bg-transparent hover:border-white transition-all duration-300"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            data-testid="hero-shop-collection-btn"
          >
            Shop Collection
          </motion.button>
          <motion.button
            onClick={scrollToNewArrivals}
            className="px-8 py-4 bg-transparent text-white font-body text-sm tracking-widest uppercase border border-[#D4AF37] hover:bg-[#D4AF37] hover:text-black transition-all duration-300"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            data-testid="hero-explore-arrivals-btn"
          >
            Explore New Arrivals
          </motion.button>
        </motion.div>
      </motion.div>

      {/* Scroll Indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-white cursor-pointer"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
        onClick={scrollToCollections}
        data-testid="scroll-indicator"
      >
        <ChevronDown size={32} className="text-[#D4AF37]" />
      </motion.div>
    </section>
  );
};
